import React, { useEffect, useRef } from 'react';
import { AUTHENTICATED } from 'lib/myNewsConstants';
import PropTypes from 'prop-types';
import { useFeatureFlagContext } from 'lib/ContextTypes';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { GATED_CONTENT } from 'lib/brandFeatures';
import { useVertical } from 'lib/Hooks';
import { getAuthenticationState } from 'lib/authenticationUtils';
import { stub as $t } from '@nbcnews/analytics-framework';
import { AccountLoginRegistration } from 'components/AccountLoginRegistration';
import throttle from 'lodash.throttle';
import styles from './styles.module.scss';
import { useBedrockRegistration } from '../../store';

$t('register', 'mbt_gate_click');


/**
 * GatedFrontPage component
 *
 * This component renders a gated front page that either shows a login/registration form
 * or a gate banner based on the user's authentication state and feature flags.
 *
 * @param {object} props - The component props
 * @param {string} props.pageRoute - The current page route
 * @returns {JSX.Element|null} The rendered component or null if the gate should not be shown
 */
const GatedFrontPage = ({ pageRoute }) => {
  const authenticationState = getAuthenticationState();
  const showBedrockGate = useBedrockRegistration((state) => state.showGate);

  const launchDarklyFlags = useFeatureFlagContext();
  const gateFrontPageFlag = launchDarklyFlags?.['today-gated-front'];

  const ref = useRef(null);

  const handleScroll = throttle(() => {
    if (!ref.current) return;
    const footerTopPosition = document.getElementById('hfs-footer')?.getBoundingClientRect().top;
    ref.current.style.position = footerTopPosition <= window.innerHeight ? 'absolute' : 'fixed';
  }, 10);

  useEffect(() => {
    const layoutContainers = document.querySelectorAll('.layout-container a');
    const tabindex = authenticationState !== AUTHENTICATED ? '-1' : '0';

    layoutContainers.forEach((a) => a.setAttribute('tabindex', tabindex));

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [authenticationState]);

  const { vertical } = useVertical();
  const hasGatedContentFeature = getFeatureConfigForBrand(
    GATED_CONTENT,
    vertical,
  );

  const showGate = (pageRoute && pageRoute === '/start-today' && hasGatedContentFeature)
      && showBedrockGate && gateFrontPageFlag;
  if (!showGate) return null;

  return (
    <div
      className={styles.container}
      data-testid="gated-front-page"
      ref={ref}
    >
      <div className={styles.backdrop} />
      <div className={styles.accountBanner}>
        <AccountLoginRegistration />
      </div>
    </div>
  );
};

GatedFrontPage.propTypes = {
  pageRoute: PropTypes.string.isRequired,
};

export default GatedFrontPage;
